import { Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription, throwError } from 'rxjs';
import { catchError, finalize, throttleTime } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CordovaService } from 'src/app/core/cordova.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { KakaotalkTemplatesService } from 'src/app/parameters/message-templates/kakaotalk-templates/kakaotalk-templates.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { AbstractComponentWithScrollContainer } from 'src/app/shared/components/abstract-component-with-scroll-container';
import { ChooseTemplateFlowService } from 'src/app/shared/components/choose-template-flow/shared/choose-template-flow.service';
import { EngagementTemplateFlowStepEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-template-flow-step.enum';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ScrollService } from 'src/app/shared/services/scroll.service';
import { ChunksAndIds } from 'src/app/task-process/chunks-and-ids.class';
import { MessageInTaskProcessComponent } from 'src/app/task-process/message-in-task-process/message-in-task-process.component';
import { PublicContextAndTaskActionsService } from 'src/app/task-process/public-context-and-task-actions/public-context-and-task-actions.service';
import {
  AnswerAction,
  AnyTaskOrActionType,
  LazyloadTasksOrActionsResponse,
  TaskForProcessingResponse,
} from 'src/app/task-process/shared/model';
import { TaskProcessUpdatesService } from 'src/app/task-process/shared/task-process-updates.service';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { environment } from 'src/environments/environment';
import { EngagementService } from './engagement.service';

@Component({
  selector: 'app-engagement',
  templateUrl: './engagement.component.html',
  styleUrls: ['./engagement.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EngagementComponent extends AbstractComponentWithScrollContainer implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef<HTMLInputElement>;
  @ViewChild(MessageInTaskProcessComponent) firstMessageInTaskProcess: MessageInTaskProcessComponent;

  @HostBinding('style.--tpp-scrollbar-width') scrollbarWidth = '0px';

  chunks: ChunksAndIds;

  templates = [];

  isInsertingChunk = false; // yet another tentative to prevent scroll jumps when lazyloading chunks
  infiniteScrollEnabled: boolean;
  hasScrolledToProcessedTask = false; // only scroll to processed task when the page is first displayed
  // (as opposed to when appending tasks/actions from ws notifs)
  isProcesedMessageBelowViewport = false;
  isProcesedMessageAboveViewport = false;
  isExpanded = true;

  noMoreMessages = false;
  shouldShowHeader = !environment.bundledWithAngularJSApp;
  ionContentShouldBeFullscreen = environment.isNativeApp;

  private subscriptions = new Subscription();

  channelType!: SiteTypeEnum;

  SiteTypeEnum = SiteTypeEnum;

  EngagementTemplateFlowStepEnum = EngagementTemplateFlowStepEnum;

  constructor(
    public engagementService: EngagementService,
    public chooseTemplateFlowService: ChooseTemplateFlowService,
    private route: ActivatedRoute,
    private taskProcessService: TaskProcessService,
    private taskProcessUpdatesService: TaskProcessUpdatesService,
    private basicService: BasicService,
    private publicContextAndTaskActionsService: PublicContextAndTaskActionsService,
    private cordovaService: CordovaService,
    private scrollService: ScrollService,
    private sitesService: SitesService,
    private alcmeonContextService: AlcmeonContextService,
    private whatsappTemplatesService: WhatsappTemplatesService,
    private kakaotalkTemplatesService: KakaotalkTemplatesService,
    customRouter: CustomRouter
  ) {
    super();
    let defaultSite: SiteV2;
    if (environment.bundledWithAngularJSApp) {
      if (!customRouter.params || !customRouter.params.siteId) {
        engagementService.initializeAccountByDefault();
      }
    } else if (route) {
      const { params } = route.snapshot;
      if (params) {
        const { siteId } = params;
        if (!siteId) {
          // engagement/send-message/search
          defaultSite = engagementService.initializeAccountByDefault();
        }
      }
    }
    engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_RECIPIENT_SENDER;
    this.chunks = new ChunksAndIds(alcmeonContextService, this.taskProcessService);
    const { isCRMMode } = this.route.snapshot.data;

    if (!environment.bundledWithAngularJSApp && route) {
      // CRM Mode (ActivatedRoute works with Angular application => widget SF)
      const { params, queryParams } = route.snapshot;
      if (params) {
        const { siteId, userExternalId } = params;
        const { crm } = queryParams;
        const siteIdUsed = siteId || defaultSite.id;
        if (!siteIdUsed) {
          console.error('No sites found');
          return;
        }
        engagementService.initContext(+siteIdUsed, userExternalId, crm);
      }
    }
    this.engagementService.strategy.initTaskProcessContext(isCRMMode);

    this.subscriptions.add(
      this.engagementService.strategy.initContextBS.subscribe(value => {
        if (value) {
          this.tryToSearchConversation(value);
        }
      })
    );
    this.subscriptions.add(engagementService.strategy.showComposerBS.subscribe(value => (this.isExpanded = value)));
  }

  ionViewDidEnter() {
    this.engagementService.strategy.taskProcessContext.taskProcessPageDidEnter$.next(true);
  }

  ngOnInit() {
    this.cordovaService.statusBarBlackText();
    this.updateInfiniteScrollEnabledAndNoMoreMessages();
    this.subscriptions.add(
      this.engagementService.strategy.taskProcessContext.doScrollToTaskOrAction$
        .pipe(throttleTime(this.scrollService.scrollDurationMs()))
        .subscribe(({ taskOrActionId, shouldBlink }) => {
          if (taskOrActionId) {
            this.scrollToTaskOrAction(taskOrActionId, shouldBlink);
          }
        })
    );
  }

  tryToSearchConversation({ userExternalId, siteId }) {
    this.channelType = this.sitesService.sitesV2Map.get(siteId).typeName;
    switch (this.channelType) {
      case SiteTypeEnum.WhatsApp:
        this.tryToSearchConversationForWhatsapp({ siteId, userExternalId });
        break;
      case SiteTypeEnum.Kakaotalk:
        this.tryToSearchConversationForKakaotalk({ siteId, userExternalId });
        break;
    }
  }

  tryToSearchConversationForWhatsapp({ userExternalId, siteId }) {
    this.chunks = new ChunksAndIds(this.alcmeonContextService, this.taskProcessService);
    this.basicService.showSpinner();
    this.whatsappTemplatesService
      .getAnswerTemplatesForEngagement(siteId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(templates => {
        this.templates = templates;
        if (userExternalId) {
          this.basicService.showSpinner();
          this.taskProcessService
            .tryToSearchConversation(userExternalId, siteId)
            .pipe(finalize(() => this.basicService.hideSpinner()))
            .subscribe({
              next: response => {
                if (!this.engagementService.strategy) {
                  return;
                }
                this.engagementService.strategy.taskProcessContext.from = response.from;
                this.initWithTask(response);
              },
            });
        }
      });
  }

  tryToSearchConversationForKakaotalk({ userExternalId, siteId }) {
    this.chunks = new ChunksAndIds(this.alcmeonContextService, this.taskProcessService);
    this.basicService.showSpinner();
    this.kakaotalkTemplatesService
      .getAnswerTemplatesForEngagement(siteId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(templates => {
        this.templates = templates;
        if (userExternalId) {
          this.basicService.showSpinner();
          this.taskProcessService
            .tryToSearchConversation(userExternalId, siteId)
            .pipe(finalize(() => this.basicService.hideSpinner()))
            .subscribe({
              next: response => {
                if (!this.engagementService.strategy) {
                  return;
                }
                this.engagementService.strategy.taskProcessContext.from = response.from;
                this.initWithTask(response);
              },
            });
        }
      });
  }

  getTemplatesForAccount(site: SiteV2) {
    const { typeName, id } = site;
    if (id) {
      this.chooseTemplateFlowService.taskProcessContext = this.engagementService.strategy.taskProcessContext;

      switch (typeName) {
        case SiteTypeEnum.WhatsApp:
          this.basicService.showSpinner();
          this.whatsappTemplatesService
            .getAnswerTemplatesForEngagement(this.engagementService.strategy.taskProcessContext.site.id)
            .pipe(finalize(() => this.basicService.hideSpinner()))
            .subscribe(templates => {
              this.templates = templates;
            });
          break;

        case SiteTypeEnum.Kakaotalk:
          this.basicService.showSpinner();
          this.kakaotalkTemplatesService
            .getAnswerTemplatesForEngagement(this.engagementService.strategy.taskProcessContext.site.id)
            .pipe(finalize(() => this.basicService.hideSpinner()))
            .subscribe(templates => {
              this.templates = templates;
            });
          break;

        default:
          return;
      }
    }
  }

  private initWithTask(response: TaskForProcessingResponse) {
    response.engagement = true;
    this.engagementService.strategy.taskProcessContext.initWithTask(response);
    this.chunks.init(response);
    this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunkPlusOneMessage(), null);
    this.engagementService.strategy.taskProcessContext.site = this.sitesService.sitesV2Map.get(
      this.engagementService.strategy.taskProcessContext.site.id
    );
    this.taskProcessUpdatesService.setContext(this.engagementService.strategy.taskProcessContext, this.chunks);
    setTimeout(() => {
      const messagesContainer = document.querySelector('app-engagement > ion-content > div.messages-container');
      messagesContainer?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }, 500);
  }

  onScrollContainerReady() {
    this.engagementService.strategy.taskProcessContext.taskProcessPageWithScrollContainer = this;
    this.scrollbarWidth = `${this.scrollContainer.offsetWidth - this.scrollContainer.clientWidth}px`;
  }

  loadNextTasksOrActions(event) {
    // let ion-infinite-scroll know when the loading is done
    this.loadMoreTasksOrActions(this.chunks.nextTasksIds(), () => event.target.complete());
  }

  private loadMoreTasksOrActions(taskOrActionIds, onCompleteFn) {
    this.taskProcessService
      .lazyloadTasksForProcessing(taskOrActionIds)
      .pipe(
        catchError(error => {
          onCompleteFn();
          return throwError(error);
        })
      )
      .subscribe(response => this.updateChunksAndKeepScroll(response, onCompleteFn));
  }

  retrySendingAnswer(answer: AnswerAction) {
    this.basicService.showSpinner();
    this.taskProcessService
      .retrySendingAnswer(answer)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(() => {
        this.basicService.toastSuccess('m/task-process/answer-sent-sucessfully');
      });
  }

  didScrollToProcessedTask() {
    this.hasScrolledToProcessedTask = true;
    this.updateInfiniteScrollEnabledAndNoMoreMessages();
    if (this.engagementService.strategy.taskProcessContext.isPublic() && environment.bundledWithAngularJSApp) {
      this.publicContextAndTaskActionsService.showPublicContextAndTaskActionsLog(
        this.engagementService.strategy.taskProcessContext,
        this.chunks.processedTask,
        true
      );
    }
  }

  private async scrollToTaskOrAction(taskOrActionId: string, shouldBlink: boolean) {
    await this.lazyLoadToTaskOrActionIfNotLoaded(taskOrActionId);
    // The aim is that we can just see either the day separator or the message immediately above
    // On desktop, this also makes the tooltip of <app-archive-and-other-actions> not cropped.
    const correction = 50;
    const elementId = this.taskProcessService.bubbleElementId(taskOrActionId);
    setTimeout(() => this.scrollService.scrollIonContentToElement(this, elementId, correction));
    if (shouldBlink) {
      const scrollDuration = this.scrollService.scrollDurationMs();
      setTimeout(
        () => this.engagementService.strategy.taskProcessContext.doBlinkBubble$.next(taskOrActionId),
        scrollDuration
      );
    }
  }

  private async lazyLoadToTaskOrActionIfNotLoaded(taskOrActionId) {
    return new Promise<void>(resolve => {
      if (this.chunks.isLoaded(taskOrActionId)) {
        resolve();
      } else {
        this.loadMoreTasksOrActions(this.chunks.idsUpTo(taskOrActionId), resolve);
      }
    });
  }

  onScrollEnd() {
    const elementId = this.taskProcessService.bubbleElementId(
      this.engagementService.strategy?.taskProcessContext.processedTaskOrActionId
    );
    const element = this.basicService.document.getElementById(elementId);
    if (element) {
      // on mobile the ion-content seems to trigger the event before the scroll to process message
      const viewportHeight = this.scrollContainer.clientHeight;
      const topOfViewport = this.scrollContainer.scrollTop;
      const bottomOfViewPort = this.scrollContainer.scrollTop + viewportHeight;
      const topOfElement = element.offsetTop;
      const bottomOfElement = element.offsetTop + element.offsetHeight;

      // The hard-coded numbers below come from trial and error: I couldn't figure out why we need them
      this.isProcesedMessageBelowViewport = bottomOfViewPort + 50 < topOfElement;
      this.isProcesedMessageAboveViewport = topOfViewport - 125 > bottomOfElement;
    }
  }

  trackById = (index, message: AnyTaskOrActionType) => message?.id;

  private updateInfiniteScrollEnabledAndNoMoreMessages() {
    this.infiniteScrollEnabled = this.chunks.hasMoreTasksToLoad();
    this.noMoreMessages = this.chunks.loadedChunks.length > 0 && !this.chunks.hasMoreTasksToLoad();
  }

  private updateChunksAndKeepScroll(response: LazyloadTasksOrActionsResponse, onCompleteFn) {
    const firstMessageBeforeUpdate = this.firstMessageInTaskProcess?.elRef;
    this.isInsertingChunk = true;
    this.chunks.updateWithOlderMessages(response);
    this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunk(), null);

    // first timeout to prevent the browser from trying to manage the scroll itself,
    // and to give it a chance to render the new messages in the new, 'position: absolute' chunk
    setTimeout(() => {
      // We want to measure how much the first message moves away from the content-container as we add the new chunk,
      // and substract that from the scroll so that it stays at the same place in the viewport
      // (we can't use the new chunk size, because the day indicator at the top of the current chunk might disappear)
      const offsetBeforeUpdate = this.distanceBetweenElementRefs(this.messagesContainer, firstMessageBeforeUpdate);

      // update previous last message's day indicator
      this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunkPlusOneMessage(), null);

      // the 'no older message' div, if present, will also change the chunk's offset; let's update it now
      this.updateInfiniteScrollEnabledAndNoMoreMessages();

      // this will remove 'position: absolute' from the first chunk
      this.isInsertingChunk = false;

      // let ionic know that lazyloading has complete (this will hide the spinner)
      onCompleteFn();

      // another timeout to give angular a chance to update the DOM
      setTimeout(() => {
        const offsetAfterUpdate = this.distanceBetweenElementRefs(this.messagesContainer, firstMessageBeforeUpdate);
        this.scrollService.adjustScrollForPage(this, offsetAfterUpdate - offsetBeforeUpdate);
      }, 0);
    }, 250);
  }

  private distanceBetweenElementRefs(elementRef1, elementRef2) {
    if (!elementRef2) {
      return elementRef1.nativeElement.offsetTop;
    }
    return elementRef2.nativeElement.offsetTop - elementRef1.nativeElement.offsetTop;
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.engagementService.clearContext();
    this.chooseTemplateFlowService.reset();
    this.taskProcessUpdatesService.clearContext(this.engagementService.strategy.taskProcessContext, this.chunks);
    if (!this.route?.snapshot?.data?.isCRMMode) {
      delete this.engagementService.strategy;
      delete this.engagementService['_channelType'];
    }
  }
}
